import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { todayLocal } from "@/lib/timefmt";

const DOWS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function EntryCalendar({ value, onChange, marked = new Set(), testId = "entry-calendar" }) {
  const init = value ? new Date(`${value}T00:00:00`) : new Date();
  const [view, setView] = useState({ y: init.getFullYear(), m: init.getMonth() });
  const today = todayLocal();
  const p = (n) => String(n).padStart(2, "0");
  const first = new Date(view.y, view.m, 1);
  const startDow = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [...Array(startDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const shift = (d) => {
    const m = view.m + d;
    const y = view.y + Math.floor(m / 12);
    setView({ y, m: ((m % 12) + 12) % 12 });
  };

  return (
    <div className="w-72 p-3 rounded-lg border border-slate-700 bg-slate-900 shadow-xl" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => shift(-1)} data-testid={`${testId}-prev`}
          className="h-7 w-7 grid place-items-center rounded hover:bg-slate-800"><ChevronLeft className="h-4 w-4" /></button>
        <div className="text-sm font-heading font-semibold">{MONTHS[view.m]} {view.y}</div>
        <button type="button" onClick={() => shift(1)} data-testid={`${testId}-next`}
          className="h-7 w-7 grid place-items-center rounded hover:bg-slate-800"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {DOWS.map((d) => <div key={d} className="text-[9px] uppercase tracking-wider text-slate-400 font-mono-num py-1">{d}</div>)}
        {cells.map((day, i) => {
          if (!day) return <div key={`x${i}`} />;
          const ds = `${view.y}-${p(view.m + 1)}-${p(day)}`;
          const isFuture = ds > today;
          const isSel = ds === value;
          const hasEntry = marked.has(ds);
          return (
            <button key={ds} type="button" disabled={isFuture} onClick={() => onChange(ds)}
              data-testid={`${testId}-day-${ds}`}
              className={`relative h-8 rounded text-xs transition-colors ${
                isSel ? "bg-blue-600 text-white font-semibold"
                : isFuture ? "text-slate-700 cursor-not-allowed"
                : hasEntry ? "bg-blue-600/15 text-blue-300 font-medium hover:bg-blue-600/25"
                : "text-slate-300 hover:bg-slate-800"}`}>
              {day}
              {hasEntry && !isSel && <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-blue-400" />}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[10px] text-slate-400">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-400" /> day with an entry
      </div>
    </div>
  );
}
